/**
 * Video Call Development Tools
 *
 * This module attaches all video call development/testing utilities to the window object
 * for easy access during development.
 *
 * Usage:
 *   Import this module in your app's entry point (or a dev-only module):
 *   import '@/utils/videoCallDevTools';
 *
 * Then in browser console:
 *   __vc.tests.runAllTests()
 *   __vc.mock.simulateIncomingCall('user-123')
 *   __vc.state.getState()
 */

import { attachToWindow as attachTestUtils } from "./videoCallTestUtils";
import { attachTestsToWindow as attachTests } from "./videoCallTests";

interface VideoCallDevTools {
  /** Test utilities for simulating socket events */
  mock: typeof import("./videoCallTestUtils").mockSocketEvents;
  /** Call state debugging utilities */
  state: typeof import("./videoCallTestUtils").callStateDebug;
  /** Network debugging utilities */
  network: typeof import("./videoCallTestUtils").networkDebug;
  /** Reconnection testing utilities */
  reconnection: typeof import("./videoCallTestUtils").reconnectionDebug;
  /** Unit test runner */
  tests: {
    runAllTests: () => Promise<void>;
    callStateManagerTests: () => Promise<any>;
    reconnectionManagerTests: () => Promise<any>;
  };
}

/**
 * Initialize all development tools
 */
export const initDevTools = (): void => {
  // Only initialize in development
  if (import.meta.env.PROD) {
    console.log("[VideoCall DevTools] Skipped in production");
    return;
  }

  // Attach test utilities
  attachTestUtils();
  attachTests();

  // Create unified interface
  const devTools: VideoCallDevTools = {
    mock: (window as any).__videoCallTestUtils?.mockSocketEvents,
    state: (window as any).__videoCallTestUtils?.callStateDebug,
    network: (window as any).__videoCallTestUtils?.networkDebug,
    reconnection: (window as any).__videoCallTestUtils?.reconnectionDebug,
    tests: (window as any).__videoCallTests,
  };

  (window as any).__vc = devTools;

  console.log(
    "%c🎥 Video Call DevTools Ready",
    "color: #F97316; font-size: 14px; font-weight: bold; background: #1F2937; padding: 4px 8px; border-radius: 4px;"
  );
  console.log("");
  console.log("%cAvailable commands:", "color: #9CA3AF; font-weight: bold;");
  console.log("%c  __vc.tests.runAllTests()", "color: #60A5FA;");
  console.log("%c  __vc.mock.simulateIncomingCall('user-id')", "color: #60A5FA;");
  console.log("%c  __vc.mock.simulateCallAccepted('request-id')", "color: #60A5FA;");
  console.log("%c  __vc.state.getState()", "color: #60A5FA;");
  console.log("%c  __vc.state.getHistory()", "color: #60A5FA;");
  console.log("%c  __vc.network.simulateOffline()", "color: #60A5FA;");
  console.log("%c  __vc.network.simulateOnline()", "color: #60A5FA;");
  console.log("");
};

// Auto-initialize if this module is imported
if (typeof window !== "undefined") {
  // Delay initialization to ensure all modules are loaded
  setTimeout(initDevTools, 100);
}

export default initDevTools;
